import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { 
  Maximize2, Minimize2, RotateCcw, Download, Plus, X, Play, Pause,
  Heart, Sparkles, Timer, ChevronRight, ChevronLeft, Volume2
} from 'lucide-react';
import { toast } from 'sonner';
import FeelingsNeedsHelper from '../FeelingsNeedsHelper';
import FloatingFeelingsHelper from '../shared/FloatingFeelingsHelper';
import {
  FeelIcon, NeedIcon, SaveIcon, ArrowLeftIcon, ArrowRightIcon
} from '../icons/ElchmeIcons';

// Process steps
const STEPS = [
  { id: 'intro', title: 'Welcome', icon: '✨', color: 'purple', description: 'Set up your alchemy session' },
  { id: 'observations', title: 'Observations', icon: '👁️', color: 'slate', description: 'Share what happened (no judgment)' },
  { id: 'needs', title: 'The Green', icon: '🌱', color: 'emerald', description: 'Discover the needs alive' },
  { id: 'feelings', title: 'The Red', icon: '❤️', color: 'rose', description: 'Express feelings with vulnerability' },
  { id: 'gold', title: 'The Gold', icon: '🏆', color: 'amber', description: 'Find mutual strategies' },
  { id: 'celebrate', title: 'Celebrate', icon: '🎉', color: 'pink', description: 'Honor the connection' }
];

export default function FishbowlInteractive() {
  const navigate = useNavigate();
  const [isHelperOpen, setIsHelperOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [activePerson, setActivePerson] = useState(null); // 'person1' or 'person2'
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [showSpeechBubble, setShowSpeechBubble] = useState(null);
  const [newItemText, setNewItemText] = useState('');

  // Session data
  const [sessionData, setSessionData] = useState(() => {
    const saved = localStorage.getItem('fishbowlInteractiveData');
    return saved ? JSON.parse(saved) : {
      person1: { name: '', observations: '', needs: [], feelingsMet: [], feelingsUnmet: [], strategies: [] },
      person2: { name: '', observations: '', needs: [], feelingsMet: [], feelingsUnmet: [], strategies: [] },
      witnesses: '',
      mutualStrategy: '',
      celebration: '',
      insights: ''
    };
  });

  // Timer effect
  useEffect(() => {
    let interval;
    if (timerRunning) {
      interval = setInterval(() => setTimerSeconds(prev => prev + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timerRunning]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Save progress
  const saveProgress = () => {
    localStorage.setItem('fishbowlInteractiveData', JSON.stringify(sessionData));
    toast.success('Session saved!');
  };

  const resetAll = () => {
    if (window.confirm('Reset all session data?')) {
      setSessionData({
        person1: { name: '', observations: '', needs: [], feelingsMet: [], feelingsUnmet: [], strategies: [] },
        person2: { name: '', observations: '', needs: [], feelingsMet: [], feelingsUnmet: [], strategies: [] },
        witnesses: '',
        mutualStrategy: '',
        celebration: '',
        insights: ''
      });
      localStorage.removeItem('fishbowlInteractiveData');
      setTimerSeconds(0);
      setTimerRunning(false);
      setCurrentStep(0);
      toast.success('Session reset!');
    }
  };

  // Add item to array
  const addItem = (person, category) => {
    if (!newItemText.trim()) return;
    setSessionData(prev => ({
      ...prev,
      [person]: {
        ...prev[person],
        [category]: [...prev[person][category], newItemText.trim()]
      }
    }));
    setNewItemText('');
  };

  // Remove item
  const removeItem = (person, category, index) => {
    setSessionData(prev => ({
      ...prev,
      [person]: {
        ...prev[person],
        [category]: prev[person][category].filter((_, i) => i !== index)
      }
    }));
  };

  // Fishbowl SVG Component
  const FishbowlSVG = () => {
    const step = STEPS[currentStep];
    const p1Active = activePerson === 'person1';
    const p2Active = activePerson === 'person2';

    // Get current items to display
    const getPersonItems = (person) => {
      const data = sessionData[person];
      if (currentStep === 2) return data.needs;
      if (currentStep === 3) return [...data.feelingsMet, ...data.feelingsUnmet];
      if (currentStep === 4) return data.strategies;
      return [];
    };

    return (
      <div className="relative w-full max-w-2xl mx-auto aspect-square">
        <svg viewBox="0 0 500 500" className="w-full h-full">
          <defs>
            <radialGradient id="bowlGrad" cx="50%" cy="40%" r="50%">
              <stop offset="0%" stopColor="#60A5FA" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.1" />
            </radialGradient>
            <filter id="glowEffect">
              <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
              <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
            <linearGradient id="p1Grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#34D399" />
              <stop offset="100%" stopColor="#10B981" />
            </linearGradient>
            <linearGradient id="p2Grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#A78BFA" />
              <stop offset="100%" stopColor="#8B5CF6" />
            </linearGradient>
          </defs>

          {/* Outer witness circle */}
          <circle cx="250" cy="250" r="230" fill="none" stroke="#4B5563" strokeWidth="2" strokeDasharray="8,4" opacity="0.5" />
          
          {/* Witness avatars */}
          {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => {
            const rad = (angle * Math.PI) / 180;
            const x = 250 + 210 * Math.cos(rad);
            const y = 250 + 210 * Math.sin(rad);
            return (
              <g key={i} className="animate-pulse" style={{ animationDelay: `${i * 0.2}s` }}>
                <circle cx={x} cy={y} r="18" fill="#374151" opacity="0.8" />
                <text x={x} y={y + 5} textAnchor="middle" fontSize="14">👤</text>
              </g>
            );
          })}

          {/* Fishbowl glass */}
          <ellipse cx="250" cy="250" rx="150" ry="130" fill="url(#bowlGrad)" />
          <ellipse cx="250" cy="250" rx="150" ry="130" fill="none" stroke="#60A5FA" strokeWidth="3" opacity="0.6" />

          {/* Person 1 */}
          <g 
            className={`cursor-pointer transition-all duration-300 ${p1Active ? 'scale-110' : 'hover:scale-105'}`}
            style={{ transformOrigin: '160px 250px' }}
            onClick={() => setActivePerson(activePerson === 'person1' ? null : 'person1')}
          >
            <circle cx="160" cy="250" r={p1Active ? 55 : 45} fill="url(#p1Grad)" filter={p1Active ? "url(#glowEffect)" : ""} />
            <text x="160" y="245" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">
              {sessionData.person1.name || 'Person 1'}
            </text>
            <text x="160" y="262" textAnchor="middle" fill="white" fontSize="20">
              {p1Active ? '🗣️' : '😊'}
            </text>
          </g>

          {/* Person 2 */}
          <g 
            className={`cursor-pointer transition-all duration-300 ${p2Active ? 'scale-110' : 'hover:scale-105'}`}
            style={{ transformOrigin: '340px 250px' }}
            onClick={() => setActivePerson(activePerson === 'person2' ? null : 'person2')}
          >
            <circle cx="340" cy="250" r={p2Active ? 55 : 45} fill="url(#p2Grad)" filter={p2Active ? "url(#glowEffect)" : ""} />
            <text x="340" y="245" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">
              {sessionData.person2.name || 'Person 2'}
            </text>
            <text x="340" y="262" textAnchor="middle" fill="white" fontSize="20">
              {p2Active ? '🗣️' : '😊'}
            </text>
          </g>

          {/* Connection heart */}
          <g className="animate-pulse">
            <circle cx="250" cy="250" r="25" fill="#EC4899" opacity="0.2" />
            <text x="250" y="258" textAnchor="middle" fontSize="24">💚</text>
          </g>

          {/* Step indicator at top */}
          <rect x="150" y="80" width="200" height="40" rx="20" fill={
            step.color === 'emerald' ? '#10B981' :
            step.color === 'rose' ? '#F43F5E' :
            step.color === 'amber' ? '#F59E0B' :
            step.color === 'purple' ? '#8B5CF6' :
            step.color === 'pink' ? '#EC4899' : '#64748B'
          } opacity="0.9" />
          <text x="250" y="106" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">
            {step.icon} {step.title}
          </text>

          {/* Items floating around active person */}
          {activePerson && getPersonItems(activePerson).slice(0, 4).map((item, i) => {
            const baseX = activePerson === 'person1' ? 160 : 340;
            const angles = [-60, -30, 30, 60];
            const angle = angles[i] || 0;
            const rad = (angle * Math.PI) / 180;
            const x = baseX + 80 * Math.cos(rad);
            const y = 250 + 60 * Math.sin(rad);
            const color = currentStep === 2 ? '#22C55E' : currentStep === 3 ? '#F43F5E' : '#F59E0B';
            
            return (
              <g key={i} className="animate-in fade-in zoom-in duration-300">
                <rect x={x - 35} y={y - 12} width="70" height="24" rx="12" fill={color} opacity="0.9" />
                <text x={x} y={y + 5} textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">
                  {item.length > 10 ? item.slice(0, 8) + '...' : item}
                </text>
              </g>
            );
          })}

          {/* Click hint */}
          {!activePerson && currentStep > 0 && currentStep < 5 && (
            <text x="250" y="400" textAnchor="middle" fill="#9CA3AF" fontSize="11">
              👆 Click a person to add their {currentStep === 2 ? 'needs' : currentStep === 3 ? 'feelings' : 'strategies'}
            </text>
          )}

          {/* Labels */}
          <text x="250" y="30" textAnchor="middle" fill="#9CA3AF" fontSize="11" fontWeight="bold">
            WITNESSES HOLDING SPACE
          </text>
          <text x="250" y="470" textAnchor="middle" fill="#9CA3AF" fontSize="10">
            Click on Person 1 or Person 2 to interact
          </text>
        </svg>

        {/* Speech Bubble for active person */}
        {activePerson && currentStep === 1 && (
          <div className={`absolute ${activePerson === 'person1' ? 'left-10' : 'right-10'} top-1/3 bg-white rounded-2xl p-4 shadow-xl max-w-xs animate-in slide-in-from-bottom`}>
            <div className="text-slate-800 text-sm">
              <strong>{sessionData[activePerson].name || (activePerson === 'person1' ? 'Person 1' : 'Person 2')}:</strong>
              <p className="mt-1 text-slate-600 italic">
                {sessionData[activePerson].observations || 'Click to share observations...'}
              </p>
            </div>
            <div className={`absolute ${activePerson === 'person1' ? 'right-[-8px]' : 'left-[-8px]'} top-1/2 w-4 h-4 bg-white transform rotate-45`} />
          </div>
        )}
      </div>
    );
  };

  // Input Panel for current step
  const InputPanel = () => {
    if (!activePerson && currentStep > 0 && currentStep < 5) return null;

    const step = STEPS[currentStep];
    const personData = sessionData[activePerson] || {};
    const personName = personData.name || (activePerson === 'person1' ? 'Person 1' : 'Person 2');

    const colorClasses = {
      slate: 'from-slate-800/95 to-slate-900/95 border-slate-500',
      emerald: 'from-emerald-900/95 to-green-900/95 border-emerald-500',
      rose: 'from-rose-900/95 to-red-900/95 border-rose-500',
      amber: 'from-amber-900/95 to-orange-900/95 border-amber-500',
      purple: 'from-purple-900/95 to-indigo-900/95 border-purple-500',
      pink: 'from-pink-900/95 to-rose-900/95 border-pink-500'
    };

    // Intro step - setup names
    if (currentStep === 0) {
      return (
        <div className="max-w-2xl mx-auto bg-gradient-to-br from-purple-900/90 to-indigo-900/90 border-2 border-purple-500 rounded-2xl p-6 shadow-2xl">
          <h3 className="text-xl font-bold text-white mb-4">✨ Set Up Your Alchemy Session</h3>
          
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-sm font-bold text-emerald-300 mb-2 block">💚 Person 1 Name</label>
              <Input
                value={sessionData.person1.name}
                onChange={(e) => setSessionData(prev => ({ ...prev, person1: { ...prev.person1, name: e.target.value } }))}
                placeholder="Enter name..."
                className="bg-emerald-900/30 border-emerald-700/50 text-white"
              />
            </div>
            <div>
              <label className="text-sm font-bold text-purple-300 mb-2 block">💜 Person 2 Name</label>
              <Input
                value={sessionData.person2.name}
                onChange={(e) => setSessionData(prev => ({ ...prev, person2: { ...prev.person2, name: e.target.value } }))}
                placeholder="Enter name..."
                className="bg-purple-900/30 border-purple-700/50 text-white"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-bold text-slate-300 mb-2 block">👥 Witnesses (optional)</label>
            <Input
              value={sessionData.witnesses}
              onChange={(e) => setSessionData(prev => ({ ...prev, witnesses: e.target.value }))}
              placeholder="Who is holding space? Friends, counselor..."
              className="bg-slate-800/50 border-slate-600 text-white"
            />
          </div>

          <div className="mt-6 flex justify-center">
            <Button onClick={() => { setCurrentStep(1); setTimerRunning(true); }} className="bg-purple-600 hover:bg-purple-500 text-lg px-8 py-6 rounded-full">
              <Play className="w-5 h-5 mr-2" /> Begin Session
            </Button>
          </div>
        </div>
      );
    }

    // Observations step
    if (currentStep === 1 && activePerson) {
      return (
        <div className={`max-w-xl mx-auto bg-gradient-to-br ${colorClasses.slate} border-2 rounded-2xl p-6 shadow-2xl`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">👁️ {personName}'s Observations</h3>
            <Button variant="ghost" size="sm" onClick={() => setActivePerson(null)} className="text-slate-400">
              <X className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-slate-400 text-sm mb-3">Share what happened - just the facts, no judgment</p>
          <Textarea
            value={personData.observations}
            onChange={(e) => setSessionData(prev => ({ 
              ...prev, 
              [activePerson]: { ...prev[activePerson], observations: e.target.value } 
            }))}
            placeholder="I observed that... When this happened..."
            className="bg-slate-800/50 border-slate-600 text-white min-h-[100px]"
          />
        </div>
      );
    }

    // Needs step
    if (currentStep === 2 && activePerson) {
      return (
        <div className={`max-w-xl mx-auto bg-gradient-to-br ${colorClasses.emerald} border-2 rounded-2xl p-6 shadow-2xl`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">🌱 {personName}'s Needs</h3>
            <Button variant="ghost" size="sm" onClick={() => setActivePerson(null)} className="text-slate-400">
              <X className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-emerald-300 text-sm mb-3">What needs are alive? Use the Feelings & Needs Tool for inspiration!</p>
          
          <div className="flex gap-2 mb-3">
            <Input
              value={newItemText}
              onChange={(e) => setNewItemText(e.target.value)}
              placeholder="Add a need..."
              className="bg-emerald-900/30 border-emerald-700/50 text-white"
              onKeyDown={(e) => e.key === 'Enter' && addItem(activePerson, 'needs')}
            />
            <Button onClick={() => addItem(activePerson, 'needs')} className="bg-emerald-600 hover:bg-emerald-500">
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            {personData.needs?.map((item, i) => (
              <span key={i} className="bg-emerald-600/60 text-white px-3 py-1.5 rounded-full text-sm flex items-center gap-2">
                🌱 {item}
                <button onClick={() => removeItem(activePerson, 'needs', i)} className="hover:text-red-300">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>

          <div className="mt-4">
            <Button onClick={() => setIsHelperOpen(true)} variant="outline" className="border-emerald-500 text-emerald-300 w-full">
              <NeedIcon size={16} className="mr-2" /> Open Needs Inventory
            </Button>
          </div>
        </div>
      );
    }

    // Feelings step
    if (currentStep === 3 && activePerson) {
      return (
        <div className={`max-w-xl mx-auto bg-gradient-to-br ${colorClasses.rose} border-2 rounded-2xl p-6 shadow-2xl`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">❤️ {personName}'s Feelings</h3>
            <Button variant="ghost" size="sm" onClick={() => setActivePerson(null)} className="text-slate-400">
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Feelings Met */}
            <div>
              <label className="text-sm font-bold text-amber-300 mb-2 block">😊 When needs MET:</label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={newItemText}
                  onChange={(e) => setNewItemText(e.target.value)}
                  placeholder="Add feeling..."
                  className="bg-amber-900/30 border-amber-700/50 text-white text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && addItem(activePerson, 'feelingsMet')}
                />
                <Button onClick={() => addItem(activePerson, 'feelingsMet')} size="sm" className="bg-amber-600">
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-1">
                {personData.feelingsMet?.map((item, i) => (
                  <span key={i} className="bg-amber-600/50 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
                    {item}
                    <button onClick={() => removeItem(activePerson, 'feelingsMet', i)}><X className="w-2 h-2" /></button>
                  </span>
                ))}
              </div>
            </div>

            {/* Feelings Unmet */}
            <div>
              <label className="text-sm font-bold text-rose-300 mb-2 block">😔 When needs NOT met:</label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={newItemText}
                  onChange={(e) => setNewItemText(e.target.value)}
                  placeholder="Add feeling..."
                  className="bg-rose-900/30 border-rose-700/50 text-white text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && addItem(activePerson, 'feelingsUnmet')}
                />
                <Button onClick={() => addItem(activePerson, 'feelingsUnmet')} size="sm" className="bg-rose-600">
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-1">
                {personData.feelingsUnmet?.map((item, i) => (
                  <span key={i} className="bg-rose-600/50 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
                    {item}
                    <button onClick={() => removeItem(activePerson, 'feelingsUnmet', i)}><X className="w-2 h-2" /></button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-4">
            <Button onClick={() => setIsHelperOpen(true)} variant="outline" className="border-rose-500 text-rose-300 w-full">
              <FeelIcon size={16} className="mr-2" /> Open Feelings List
            </Button>
          </div>
        </div>
      );
    }

    // Strategies step
    if (currentStep === 4 && activePerson) {
      return (
        <div className={`max-w-xl mx-auto bg-gradient-to-br ${colorClasses.amber} border-2 rounded-2xl p-6 shadow-2xl`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">🏆 {personName}'s Strategies</h3>
            <Button variant="ghost" size="sm" onClick={() => setActivePerson(null)} className="text-slate-400">
              <X className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-amber-300 text-sm mb-3">What strategies or requests would meet these needs?</p>
          
          <div className="flex gap-2 mb-3">
            <Input
              value={newItemText}
              onChange={(e) => setNewItemText(e.target.value)}
              placeholder="Add a strategy or request..."
              className="bg-amber-900/30 border-amber-700/50 text-white"
              onKeyDown={(e) => e.key === 'Enter' && addItem(activePerson, 'strategies')}
            />
            <Button onClick={() => addItem(activePerson, 'strategies')} className="bg-amber-600 hover:bg-amber-500">
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            {personData.strategies?.map((item, i) => (
              <span key={i} className="bg-amber-600/60 text-white px-3 py-1.5 rounded-full text-sm flex items-center gap-2">
                🏆 {item}
                <button onClick={() => removeItem(activePerson, 'strategies', i)} className="hover:text-red-300">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      );
    }

    // Gold step - Mutual Strategy
    if (currentStep === 4 && !activePerson) {
      return (
        <div className="max-w-xl mx-auto bg-gradient-to-br from-amber-900/95 to-yellow-900/95 border-2 border-amber-500 rounded-2xl p-6 shadow-2xl">
          <h3 className="text-xl font-bold text-amber-300 mb-4">🏆 The Gold: Mutual Strategy</h3>
          <p className="text-amber-200/80 text-sm mb-4">
            Having heard both perspectives, what strategy honors BOTH sets of needs?
          </p>
          <Textarea
            value={sessionData.mutualStrategy}
            onChange={(e) => setSessionData(prev => ({ ...prev, mutualStrategy: e.target.value }))}
            placeholder="A strategy that works for both of us would be..."
            className="bg-amber-900/40 border-amber-500/50 text-white min-h-[120px] text-lg"
          />
          <p className="text-amber-300/70 text-xs mt-3 italic">
            💡 Tip: Click on Person 1 or Person 2 to add their individual strategies first
          </p>
        </div>
      );
    }

    // Celebration step
    if (currentStep === 5) {
      return (
        <div className="max-w-xl mx-auto bg-gradient-to-br from-pink-900/95 to-purple-900/95 border-2 border-pink-500 rounded-2xl p-6 shadow-2xl">
          <h3 className="text-xl font-bold text-pink-300 mb-4">🎉 Celebration & Closure</h3>
          
          <div className="mb-4">
            <label className="text-sm font-bold text-purple-300 mb-2 block">✨ What are you celebrating?</label>
            <Textarea
              value={sessionData.celebration}
              onChange={(e) => setSessionData(prev => ({ ...prev, celebration: e.target.value }))}
              placeholder="The courage shown, the connection made, the vulnerability shared..."
              className="bg-pink-900/30 border-pink-700/50 text-white min-h-[80px]"
            />
          </div>

          <div>
            <label className="text-sm font-bold text-teal-300 mb-2 block">💡 Key Insights</label>
            <Textarea
              value={sessionData.insights}
              onChange={(e) => setSessionData(prev => ({ ...prev, insights: e.target.value }))}
              placeholder="What did we learn? What will we do differently?"
              className="bg-teal-900/30 border-teal-700/50 text-white min-h-[80px]"
            />
          </div>

          <div className="mt-6 text-center">
            <p className="text-pink-200 italic mb-4">
              "The vulnerability leads to potency — paradoxically!" ✨
            </p>
            <div className="flex justify-center gap-4">
              <Button onClick={() => navigate('/workbook/compassionate-wheel-interactive')} variant="outline" className="border-emerald-500 text-emerald-300">
                ← Try Communication Wheel
              </Button>
              <Button onClick={() => navigate('/belonging-ebook')} className="bg-pink-600 hover:bg-pink-500">
                Return to eBook
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  // Step Progress Bar
  const StepProgress = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {STEPS.map((step, i) => (
        <button
          key={step.id}
          onClick={() => i <= currentStep && setCurrentStep(i)}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs transition-all ${
            i === currentStep
              ? 'bg-white text-slate-900 font-bold scale-110'
              : i < currentStep
                ? 'bg-emerald-600 text-white cursor-pointer hover:bg-emerald-500'
                : 'bg-slate-700 text-slate-400'
          }`}
        >
          {step.icon}
          {i === currentStep && <span className="hidden sm:inline">{step.title}</span>}
        </button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur-md border-b border-slate-700/50">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <ArrowLeftIcon size={16} className="mr-1" /> Back
              </Button>
              <div className="h-5 w-px bg-slate-700" />
              <div>
                <h1 className="text-lg font-bold text-white">Interactive Fishbowl</h1>
                <p className="text-xs text-purple-400">Empathetic Alchemy • Click the people to interact!</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Timer */}
              {currentStep > 0 && (
                <div className="bg-slate-800 rounded-lg px-3 py-1.5 flex items-center gap-2">
                  <Timer className="w-4 h-4 text-amber-400" />
                  <span className="text-lg font-mono text-white">{formatTime(timerSeconds)}</span>
                  <button onClick={() => setTimerRunning(!timerRunning)} className={timerRunning ? 'text-rose-400' : 'text-emerald-400'}>
                    {timerRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                </div>
              )}
              
              <Button onClick={saveProgress} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <SaveIcon size={16} className="mr-1" /> Save
              </Button>
              <Button onClick={resetAll} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* Step Progress */}
        <StepProgress />

        {/* Fishbowl Visualization */}
        <FishbowlSVG />

        {/* Input Panel */}
        <div className="mt-6">
          <InputPanel />
        </div>

        {/* Navigation */}
        {currentStep > 0 && currentStep < 5 && (
          <div className="flex justify-center gap-4 mt-6">
            <Button onClick={() => { setCurrentStep(prev => prev - 1); setActivePerson(null); }} variant="outline" className="border-slate-600 text-slate-300">
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous Step
            </Button>
            <Button onClick={() => { setCurrentStep(prev => prev + 1); setActivePerson(null); }} className="bg-purple-600 hover:bg-purple-500">
              Next Step <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {/* Feelings & Needs CTA */}
        {currentStep > 0 && currentStep < 5 && (
          <div className="flex justify-center mt-6">
            <Button
              onClick={() => setIsHelperOpen(true)}
              className="relative bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 text-white"
            >
              <div className="absolute inset-0 rounded-md bg-rose-500/30 animate-ping" style={{ animationDuration: '2s' }} />
              <span className="relative flex items-center gap-2">
                <FeelIcon size={18} /> Feelings & Needs Tool <NeedIcon size={18} />
              </span>
            </Button>
          </div>
        )}
      </main>

      {/* Floating Helper */}
      <FloatingFeelingsHelper />
      <FeelingsNeedsHelper isOpen={isHelperOpen} onClose={() => setIsHelperOpen(false)} />
    </div>
  );
}
